import { Localized } from "@fluent/react"
import { Autocomplete, Avatar, Box, Skeleton, SxProps, TextField, Theme, Typography } from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import { memo, ReactNode, useMemo } from "react"
import { LoadUsers } from "../../app/AppDataLoader"
import { AppId } from "../../app/appTypes"
import { useAppSelector } from "../../app/hooks"
import { selectAllUsers, User } from "./usersSlice"

export interface UserPickerProps {
    /** Controlled by user id; `null` or `''` is no selection. */
    value: AppId | null
    onChange: (user: User | null) => void
    label?: ReactNode
    required?: boolean
    readOnly?: boolean
    disabled?: boolean
    /** Hides users from the list (e.g. existing team members). The selected user always stays. */
    exclude?: (user: User) => boolean
    /** Shown when `value` is not among the users (an archived user), e.g. the name stored with it. */
    fallbackName?: string
    /** Shown under the selected user's position. */
    helperText?: ReactNode
    size?: 'small' | 'medium'
    fullWidth?: boolean
    sx?: SxProps<Theme>
}

export type UserNameOption = {
    userName: string
    name: string
    email: string
    avatarUrl?: string
    label: string
    searchText: string
}

const UserOptionAvatar = ({ name, avatarUrl }: { name: string; avatarUrl?: string }) => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    const initials = parts.length > 1
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        : (parts[0]?.[0] ?? "").toUpperCase()
    return <Avatar src={avatarUrl} sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }}>
        {initials || <PersonIcon sx={{ fontSize: 16 }} />}
    </Avatar>
}

type UserNamePickerBaseProps = {
    selectedUserName: string | null
    label: string
    disabled: boolean
    required?: boolean
    size?: "small" | "medium"
    /** Restrict choices to active department members, keeping the selected member. */
    members?: { userName: string; firstName?: string; lastName?: string; email?: string; isActive: boolean }[]
    manualOptionLabel?: string
    fallbackName?: string
    fallbackEmail?: string
}

export type UserNamePickerProps = UserNamePickerBaseProps & (
    | { readOnly: true; onChange?: never; noOptionsText?: never }
    | { readOnly?: false; onChange: (option: UserNameOption | null) => void; noOptionsText: string }
)

const pickerName = (firstName: string, surname: string) => [surname, firstName].filter(Boolean).join(" ")
const pickerLabel = (name: string, email: string) => `${name}${email ? ` (${email})` : ""}`

const createUserNameOption = (
    userName: string, name: string, email: string, avatarUrl?: string, displayName = name,
): UserNameOption => {
    const label = pickerLabel(displayName || userName, email)
    return {
        userName, name: name || userName, email, avatarUrl, label,
        searchText: `${name} ${userName} ${email}`.toLocaleLowerCase(),
    }
}

const filterUserNameOptions = (options: UserNameOption[], { inputValue }: { inputValue: string }) => {
    const query = inputValue.trim().toLocaleLowerCase()
    const result: UserNameOption[] = []
    for (const option of options) {
        if (!query || option.searchText.includes(query)) {
            result.push(option)
            if (result.length === 60) break
        }
    }
    return result
}

const UserNamePicker = memo(function UserNamePicker(props: UserNamePickerProps) {
    const {
        selectedUserName, label, disabled, required = false, size = "small",
        members, manualOptionLabel, fallbackName, fallbackEmail,
    } = props
    const allUsers = useAppSelector(selectAllUsers)
    const options = useMemo(() => {
        const usersByName = new Map(allUsers.map(user => [user.userName.toLowerCase(), user] as const))
        const available = members
            ? members.filter(member => member.isActive || member.userName === selectedUserName).map(member => {
                const user = usersByName.get(member.userName.toLowerCase())
                const firstName = member.firstName || user?.firstname || ""
                const surname = member.lastName || user?.surname || ""
                const name = `${firstName} ${surname}`.trim()
                return createUserNameOption(member.userName, name, member.email || user?.email || "",
                    user?.avatarUrl, pickerName(firstName, surname))
            })
            : allUsers.map(user => createUserNameOption(
                user.userName, `${user.firstname ?? ""} ${user.surname ?? ""}`.trim(), user.email ?? "", user.avatarUrl,
                pickerName(user.firstname ?? "", user.surname ?? ""),
            ))
        const manual = manualOptionLabel === undefined ? [] : [createUserNameOption("", manualOptionLabel, "")]
        const missing = selectedUserName && !available.some(option => option.userName === selectedUserName)
            ? [createUserNameOption(selectedUserName, fallbackName ?? "", fallbackEmail ?? "")] : []
        return [...manual, ...missing, ...available]
    }, [allUsers, members, selectedUserName, manualOptionLabel, fallbackName, fallbackEmail])
    const value = useMemo(() => options.find(option => option.userName === selectedUserName) ?? null,
        [options, selectedUserName])
    return <LoadUsers component={<Skeleton variant="rectangular" height={size === "small" ? 40 : 56} />}>
        {props.readOnly
            ? <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">{label}:</Typography>
                <Typography variant="body1">{value?.label ?? ""}</Typography>
            </Box>
            : <Autocomplete<UserNameOption>
                options={options} value={value} size={size} fullWidth disabled={disabled}
                onChange={(_, option) => props.onChange(option)}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, selected) => option.userName === selected.userName}
                filterOptions={filterUserNameOptions} noOptionsText={props.noOptionsText}
                renderOption={(optionProps, option) =>
                    <Box component="li" {...optionProps} key={option.userName} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <UserOptionAvatar name={option.userName ? option.name : ""} avatarUrl={option.avatarUrl} />
                        <Typography variant="body2">{option.label}</Typography>
                    </Box>}
                renderInput={params => <TextField {...params} label={label} required={required} />}
            />}
    </LoadUsers>
})

export const userFullName = (user: User) => `${user.firstname} ${user.surname}`

/** Company user Autocomplete: searchable by name and email, shows the selected user's position. */
const CompanyUserPicker = ({
    value, onChange, label, required, readOnly, disabled, exclude, fallbackName, helperText, size, fullWidth, sx,
}: UserPickerProps) => {
    const allUsers = useAppSelector(selectAllUsers)
    const selected = useMemo(() => (value ? allUsers.find(u => u.id === value) ?? null : null), [allUsers, value])
    const options = useMemo(
        () => (exclude ? allUsers.filter(u => u.id === selected?.id || !exclude(u)) : allUsers),
        [allUsers, exclude, selected])

    const position = selected?.position
    const helper = position || helperText
        ? <>{position && <span style={{ display: 'block' }}>{position}</span>}{helperText}</>
        : " "

    return <LoadUsers component={<Skeleton variant="rectangular" height={size === 'small' ? 40 : 56} />}>
        <Autocomplete
            sx={sx}
            size={size}
            fullWidth={fullWidth}
            readOnly={readOnly}
            disabled={disabled}
            options={options}
            value={selected}
            onChange={(_, user) => onChange(user ?? null)}
            getOptionLabel={user => pickerLabel(pickerName(user.firstname, user.surname), user.email)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, user) =>
                <Box component="li" {...props} key={user.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <UserOptionAvatar name={userFullName(user)} avatarUrl={user.avatarUrl} />
                    <Typography variant="body2">
                        {pickerLabel(pickerName(user.firstname, user.surname), user.email)}
                    </Typography>
                </Box>}
            renderInput={params => <TextField
                {...params}
                required={required}
                label={label ?? <Localized id="username"><span>_Nazwa użytkownika</span></Localized>}
                placeholder={value && !selected ? fallbackName : undefined}
                helperText={helper}
            />}
        />
    </LoadUsers>
}

export const UserPicker = (props: UserPickerProps | UserNamePickerProps) =>
    "selectedUserName" in props ? <UserNamePicker {...props} /> : <CompanyUserPicker {...props} />
